import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  hasInjectedEvm, hasInjectedSolana, connectEvm, connectSolana,
  setEvmProvider, setSolanaProvider, onEvmAccountsChanged,
  connectWalletConnect, restoreWalletConnect, isWalletConnectActive, disconnectWalletConnect,
} from "@/lib/onchain";

const WalletContext = createContext(null);
const STORAGE_KEY = "flip_wallet";

function randomEvmAddress() {
  const hex = "0123456789abcdef"; let a = "0x";
  for (let i = 0; i < 40; i++) a += hex[Math.floor(Math.random() * 16)];
  return a;
}
function randomSolAddress() {
  const chars = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz"; let a = "";
  for (let i = 0; i < 44; i++) a += chars[Math.floor(Math.random() * chars.length)];
  return a;
}

export function WalletProvider({ children }) {
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    let parsed = null;
    try { parsed = JSON.parse(stored); } catch (e) { localStorage.removeItem(STORAGE_KEY); return; }
    if (parsed?.real && parsed.provider === "WalletConnect") {
      // WC sessions survive reloads: silently restore the provider binding.
      restoreWalletConnect().then((ok) => {
        if (ok) setWallet(parsed);
        else {
          localStorage.removeItem(STORAGE_KEY);
          toast.error("WalletConnect session expired — reconnect your wallet");
        }
      });
    } else if (parsed) {
      if (parsed.real) localStorage.removeItem(STORAGE_KEY); // injected wallets re-connect on use
      else setWallet(parsed);
    }
  }, []);

  // Keep the connected EVM wallet in sync when the user switches accounts
  // directly inside their wallet extension. Re-binds whenever a different
  // provider gets selected (providerEpoch is bumped on connect).
  const [providerEpoch, setProviderEpoch] = useState(0);
  useEffect(() => {
    const off = onEvmAccountsChanged((accounts) => {
      setWallet((w) => {
        if (!w?.real || w.ecosystem !== "evm") return w;
        const addr = accounts?.[0];
        if (!addr) return null;
        if (addr.toLowerCase() === w.address.toLowerCase()) return w;
        const updated = { ...w, address: addr };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    });
    return off;
  }, [providerEpoch]);

  const save = (w) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(w));
    setWallet(w);
    return w;
  };

  // Connect a specific EIP-6963 EVM provider (from the discovered list).
  // forcePicker re-opens the wallet's account chooser so the user can pick
  // any of their accounts instead of being locked to the first one.
  const connectEvmDetail = useCallback(async (detail) => {
    setEvmProvider(detail.provider, detail.info?.name || null);
    const prev = wallet?.real && wallet.ecosystem === "evm" ? wallet.address.toLowerCase() : null;
    const r = await connectEvm(detail.provider, { forcePicker: true });
    if (prev && r.address.toLowerCase() === prev) {
      // Confirming the wallet's permissions dialog without changing the
      // selection keeps the same account — make that explicit to the user.
      toast.info("Same account re-selected. To switch: in the wallet popup, tick a different account before confirming.");
    }
    setProviderEpoch((n) => n + 1);
    return save({ address: r.address, provider: r.walletName || detail.info?.name || "Wallet", ecosystem: "evm", real: true });
  }, [wallet]);

  const connectSolanaWallet = useCallback(async (name = "Phantom") => {
    const r = await connectSolana(name);
    return save({ address: r.address, provider: name, ecosystem: "solana", real: true });
  }, []);

  // WalletConnect: QR pairing with 500+ mobile/hardware wallets. The WC provider
  // becomes the selected EVM provider, so all existing flows work unchanged.
  const connectWalletConnectWC = useCallback(async () => {
    const r = await connectWalletConnect();
    setProviderEpoch((n) => n + 1);
    return save({ address: r.address, provider: "WalletConnect", ecosystem: "evm", real: true });
  }, []);

  const connectDemo = useCallback((ecosystem = "evm") => {
    const address = ecosystem === "solana" ? randomSolAddress() : randomEvmAddress();
    return save({ address, provider: "Demo", ecosystem, real: false });
  }, []);

  // Backwards-compatible auto-connect used by ContributeModal.
  const connect = useCallback(async (providerName = "MetaMask") => {
    const isSolana = providerName === "Phantom" || providerName === "Solflare";
    try {
      if (isSolana && hasInjectedSolana()) return await connectSolanaWallet(providerName);
      if (!isSolana && hasInjectedEvm()) {
        const r = await connectEvm();
        return save({ address: r.address, provider: r.walletName || providerName, ecosystem: "evm", real: true });
      }
    } catch (e) { throw e; }
    // no injected wallet → demo fallback (real transfers will be blocked)
    return connectDemo(isSolana ? "solana" : "evm");
  }, [connectSolanaWallet, connectDemo]);

  const disconnect = useCallback(() => {
    if (isWalletConnectActive()) disconnectWalletConnect();
    localStorage.removeItem(STORAGE_KEY);
    setEvmProvider(null);
    setSolanaProvider(null);
    setWallet(null);
  }, []);

  return (
    <WalletContext.Provider value={{ wallet, connect, connectEvmDetail, connectSolanaWallet, connectWalletConnectWC, connectDemo, disconnect }}>
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext);
